import { EventEmitter, Injectable } from "@angular/core";
import { Store, select } from "@ngrx/store";
import { combineLatest, of } from "rxjs";
import { catchError, distinct, distinctUntilKeyChanged, filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";
import { ErrorState, LoadingState } from "../../root-store/api-result.state";
import { ServicePlanActions, SubscriptionActions } from "../store/actions";

import { MatDialog } from "@angular/material/dialog";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { AuthState } from "@app/auth/store";
import { CellularDevice } from "@app/cellular/models/cellular-device.model";
import { CellularService } from "@app/cellular/services/cellular.service";
import { ServicePlanOptionsComponent } from "@app/project/components/security/service-plan-options/service-plan-options.component";
import { ClareDialogComponent } from "@app/shared/components/clare-dialog/clare-dialog.component";
import { CellularProviderType } from "@app/shared/databases/cellular.database.service";
import { ClareStatus } from "@app/shared/model/clare-status.model";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { DateTime } from "luxon";
import { AppHelperService } from "../../shared/services/app-helper.service";
import { ClareServicePlan } from "../model/clare-service-plan";
import { ClareServiceSubscription } from "../model/clare-service-subscription";
import { Project } from "../model/project";
import { SERVICE_PLAN_DIALOG_VIEW, ServicePlanTiers } from "../model/service-plan";
import { ServicePlanFeature } from "../model/service-plan-feature";
import { ProjectDispatchers } from "../store/dispatchers/project.dispatchers";
import { ServicePlanState } from "../store/projects.state";
import { ServicePlanSelectors } from "../store/selectors";
import { ProjectHelperService } from "./project-helper.service";
import { ServicePlanUtils } from "./service-plan-utils";

@Injectable()
export class ServicePlansService {
    displayEditServicePlanDialog(
        // Caller needs to supply this to avoid circular dependency issues (during testing)
        component: typeof ServicePlanOptionsComponent,
        currentPlan,
        selectedPlan,
        subscription,
        project,
        cellularDevice?,
        onUpdateDone?,
        view: SERVICE_PLAN_DIALOG_VIEW = SERVICE_PLAN_DIALOG_VIEW.SELECT_PLAN
    ) {
        if (project.inactive) {
            this.displayAccountSuspendedDialog();
        } else {
            const dialogRef = this.dialog.open(component, { panelClass: "dialog-no-padding" }).componentInstance;
            dialogRef.currentPlan = currentPlan;
            dialogRef.selectedPlan = selectedPlan;
            dialogRef.subscription = subscription;
            dialogRef.project = project;
            dialogRef.cellularDevice = cellularDevice;
            dialogRef.onUpdateDone = onUpdateDone;
            dialogRef.activeView = view;
        }
    }

    private displayAccountSuspendedDialog() {
        const config = {
            id: "account-suspended",
            data: {
                title: "Account Suspended",
                message: "Account must be re-activated to allow changes to service plan.",
                loading: false,
                buttons: [
                    {
                        text: "Close",
                        method: "close",
                    },
                ],
            },
            disableClose: false,
        };
        this.dialog.open(ClareDialogComponent, config).afterClosed().pipe(take(1)).subscribe();
    }

    getBasicPlan(plans) {
        return plans.find(plan => plan.name === ServicePlanTiers.BASIC) || {};
    }

    getServicePlanStatus(plan): ClareStatus {
        return plan && plan.displayJson ? [plan.name, plan.displayJson.color] : ["", ""];
    }

    getActiveServicePlans(grandfathered: boolean = false) {
        return this.store.pipe(select(ServicePlanSelectors.getActiveServicePlans(grandfathered)));
    }

    private ignoreInitStateFilter(planState: ServicePlanState) {
        if (planState.callState === LoadingState.init || this.isError(planState.callState)) {
            this.store.dispatch(ServicePlanActions.load());
        }
        return planState.callState === LoadingState.loaded;
    }

    getServicePlans() {
        return this.store.pipe(
            select(ServicePlanSelectors.getServicePlanState),
            // Ignore update if there's still an error, to avoid refetch loop
            distinct(planState => (this.isError(planState.callState) ? "error" : planState.callState)),
            filter(planState => this.ignoreInitStateFilter(planState)),
            map(planState => Object.values(planState.result || {}))
        );
    }

    getServicePlansObj$() {
        return this.store.pipe(
            select(ServicePlanSelectors.getServicePlansObj),
            withLatestFrom(this.store.pipe(select(ServicePlanSelectors.servicePlansLoadingState))),
            distinct(([_, callState]) => (this.isError(callState) ? "error" : callState)),
            tap(([_, callState]) => {
                if (callState === LoadingState.init || this.isError(callState)) {
                    return this.store.dispatch(ServicePlanActions.load());
                }
            }),
            map(([result]) => result)
        );
    }

    private selectActiveProjectSubscription$(projectId: number) {
        return this.store.pipe(
            select(ServicePlanSelectors.activeSubscription),
            distinctUntilKeyChanged("result"),
            distinct(callState => (this.isError(callState) ? "error" : callState)),
            filter(subscriptionState => {
                if (!!subscriptionState.result?.projectId && projectId !== subscriptionState.result?.projectId) {
                    this.clearActiveSubscription();
                }
                if (subscriptionState.callState === LoadingState.init) {
                    this.store.dispatch(new SubscriptionActions.GetActiveSubscriptionByProjectId(projectId));
                }

                return (
                    subscriptionState.callState === LoadingState.loaded &&
                    (subscriptionState.result === undefined || projectId === subscriptionState.result?.projectId)
                );
            }),
            map(subscriptionState => subscriptionState.result)
        );
    }

    private selectServicePlans$() {
        return this.store.pipe(
            select(ServicePlanSelectors.getServicePlanState),
            distinct(planState => (this.isError(planState.callState) ? "error" : planState.callState)),
            filter(planState => this.ignoreInitStateFilter(planState)),
            map(planState => planState.result || {})
        );
    }

    getActiveSubscriptionAndServicePlans(projectId: number) {
        return combineLatest([this.selectActiveProjectSubscription$(projectId), this.selectServicePlans$()]).pipe(
            map(([subscription, servicePlans]) => {
                if (ServicePlanUtils.isActive(subscription)) {
                    return {
                        subscription,
                        servicePlan: servicePlans[subscription.servicePlanId],
                    };
                }

                if (subscription) {
                    return {
                        subscription: { startDate: subscription.endDate, optInFeatures: [] },
                        servicePlan: this.getBasicPlan(Object.values(servicePlans)) as ClareServicePlan,
                    };
                }

                return {};
            }),
            catchError(e => {
                console.error(e);
                return of(e);
            })
        );
    }

    clearActiveSubscription() {
        this.store.dispatch(new SubscriptionActions.ClearActiveSubscription());
    }

    clearSubscriptions() {
        this.store.dispatch(new SubscriptionActions.ClearSubscriptions());
    }

    private getEndDate() {
        return DateTime.now()
            .endOf("day")
            .setLocale("en-us")
            .set({ year: 2030 })
            .toUTC()
            .toISO()
            .toString()
            .replace(/\.\d{0,3}/, "");
    }

    private sortFeatures(features: ServicePlanFeature[] = [], plans: ClareServicePlan[] = []): ServicePlanFeature[] {
        // Attach a count to each feature (how many plans include this feature)
        const planHasFeature = (plan: ClareServicePlan, feature: ServicePlanFeature) =>
            !!plan.features.filter(f => f.featureName === feature.featureName).length;
        const counts = features.map(f => {
            const numPlans = plans.filter(p => planHasFeature(p, f)).length;
            return { feature: f, total: numPlans };
        });

        // Sort features by counts ascending
        return counts.sort((a, b) => b.total - a.total).map(d => d.feature);
    }

    getAllPlanFeatures(plans: ClareServicePlan[] = []) {
        const featureNames = [];
        const featuresList = [];
        plans.forEach(plan => {
            plan.features.forEach(feature => {
                if (!featureNames.includes(feature.featureName)) {
                    featureNames.push(feature.featureName);
                    featuresList.push({ ...feature });
                }
            });
        });

        return this.sortFeatures(featuresList, plans);
    }

    savePlans(
        subscription: ClareServiceSubscription | null,
        currentPlan: ClareServicePlan,
        updatedPlan: ClareServicePlan,
        project: Project,
        cellularDevice?: CellularDevice,
        onChangePlanDone?: EventEmitter<any>,
        additionalFeatures = [],
        ignoreIDCheck = false,
        isProjectOffline = false
    ) {
        const uuid = ProjectHelperService.getProjectUuid(project);

        if (subscription?.id) {
            const currentSubscription = { ...subscription };
            const updatedSubscription = { ...subscription };
            // const securitySystem = SecuritySystem.fromDbObject(project.securitysystem);
            const securitySystem = project.securitysystem;
            const alulaDevice$ = this.projectDispatcher.getAlulaDevice$(securitySystem?.macAddress);
            if (updatedPlan.id === currentPlan.id) {
                /**
                 * @TODO: Do we still need this update call?
                 *        It appears to have been necessary when optional features were still a thing
                 *        But now that plans with those features ("Plus" / "Premium") are gone,
                 *          (only exist on dev db, and even then its basically filtered out by selectors due to old endTime)
                 *        It seems we're always submitting with unchanged data, which is unnecessary
                 */
                if (!subscription.startDate.endsWith("Z")) {
                    // Need to append Z here because UpdateSubscription call requires it.
                    // It doesn't store as utc in db but we assume that it is.
                    updatedSubscription.startDate = subscription.startDate + "Z";
                    updatedSubscription.endDate = subscription.endDate + "Z";
                }

                const eventEmitter = new EventEmitter();

                eventEmitter.subscribe(result => {
                    onChangePlanDone?.emit(result);
                });

                this.store.dispatch(
                    new SubscriptionActions.UpdateSubscription(
                        updatedSubscription,
                        uuid,
                        updatedPlan,
                        project,
                        additionalFeatures,
                        isProjectOffline,
                        eventEmitter
                    )
                );
            } else {
                // @TODO: Switching between plans multiple times isnt working (eg Basic -> Interactive -> Basic)
                //        For the second call, the currentSubscription sent is not the most recent subscription
                //        Indicating that we are not saving the new subscription returned from the first call

                // Service Plan has changed. Update service plan in subscription
                // Cancel current subscription and create a new one. Old one will be kept in records
                const newSub = {
                    projectId: project.id,
                    servicePlanId: updatedPlan.id,
                    startDate: AppHelperService.getCurrentDateUTC(),
                    endDate: this.getEndDate(),
                };
                currentSubscription.endDate = newSub.startDate;
                const eventEmitter = new EventEmitter();
                let updatedAction = null;
                eventEmitter
                    .pipe(
                        tap(result => (updatedAction = result)),
                        switchMap(_ => this.isLTEDowngraded(currentPlan.id, updatedPlan.id))
                    )
                    .subscribe(isDowngraded => {
                        if (isDowngraded) {
                            this.setProviderToNone(project, onChangePlanDone, updatedAction, ignoreIDCheck);
                            this.updateStoredSubscription(updatedAction);
                        } else {
                            onChangePlanDone.emit(updatedAction);
                        }
                    });
                this.store.dispatch(
                    new SubscriptionActions.ConvertSubscription(
                        {
                            currentSubscription,
                            currentServicePlan: currentPlan,
                            newSubscription: newSub,
                            newServicePlan: updatedPlan,
                            alulaDevice: alulaDevice$,
                            uuid,
                            project,
                            cellularDevice,
                            additionalFeatures,
                            isProjectOffline,
                        },
                        eventEmitter
                    )
                );

                // TODO: Insert UUID and firebase_id
                this.tsLoggingService.saveAuditEventData({
                    uuid: null,
                    entity: "service plan",
                    object_type: "ssopgrest",
                    operation: "update",
                    firebase_id: null,
                    raw_body_content: {
                        project,
                        currentSubscription,
                        newSub,
                    },
                });
                const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.SERVICE_PLANS, LOG_SUB_TYPE.MODIFY);
                this.auditLogService.logEvent(logKey, {
                    data: `Original Plan: ${currentPlan.name}, New Plan: ${updatedPlan.name}`,
                    optionalParams: { uuid },
                    entityId: project.entityId,
                    projectId: project.id,
                });
            }
        } else {
            const eventEmitter = new EventEmitter();

            eventEmitter.subscribe(result => {
                onChangePlanDone?.emit(result);
            });
            const newSubscription = {
                projectId: project.id,
                servicePlanId: updatedPlan.id,
                startDate: AppHelperService.getCurrentDateUTC(),
                endDate: this.getEndDate(),
            };
            this.store.dispatch(
                new SubscriptionActions.CreateSubscription(
                    newSubscription,
                    uuid,
                    updatedPlan,
                    project,
                    additionalFeatures,
                    isProjectOffline,
                    eventEmitter
                )
            );
            // TODO: Insert UUID and firebase_id
            this.tsLoggingService.saveAuditEventData({
                uuid: null,
                entity: "service plan",
                object_type: "ssopgrest",
                operation: "add",
                firebase_id: null,
                raw_body_content: {
                    project,
                    subscription: newSubscription,
                },
            });
        }
    }

    updateStoredSubscription(action: SubscriptionActions.ConvertSubscriptionSuccess) {
        this.store.dispatch(action);
    }

    private isError(callState): callState is ErrorState<any> {
        return !!callState.error;
    }

    private isLTEDowngraded(currentServicePlanId: number, updatedPlanId: number) {
        return this.getActiveServicePlans().pipe(
            take(1),
            map(servicePlans => {
                const ltePlan = servicePlans.find(p => p.name === ServicePlanTiers.INTERACTIVE_PLUS_LTE);
                if (currentServicePlanId === ltePlan.id && updatedPlanId !== ltePlan.id) {
                    return true;
                }

                return false;
            })
        );
    }

    private setProviderToNone(
        project: Project,
        callback: EventEmitter<any>,
        action: SubscriptionActions.ConvertSubscriptionSuccess,
        ignoreIDCheck = false
    ) {
        this.cellularService
            .updateCellularDevice(project, CellularProviderType.NONE, false, true, ignoreIDCheck)
            .subscribe(_ => callback.emit(action));
    }

    constructor(
        private store: Store<AuthState | ServicePlanState>,
        private tsLoggingService: TSLoggingService,
        private dialog: MatDialog,
        private projectDispatcher: ProjectDispatchers,
        private cellularService: CellularService,
        private auditLogService: AuditLogService
    ) {}
}
